import React, { useEffect, useState } from "react";
import "./CategoryList.scss";
import axiosInstance from "../../../axios";
import { getS3URL } from "../../../_helpers";
import { FaChevronRight, FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment/moment";
import CartButton from "../../Courses/CourseTile/CartButton/CartButton";
import Share from "../../Courses/CourseShare/courseShare";
import { Heart, Share2 } from "lucide-react";
import { deleteWishlist, addWishlist } from "../../../_actions/cart.actions";
import { courseActions } from "../../../_actions";

const { REACT_APP_LOGIN_API_BASEURL } = process.env;
const slugify = (text) => text.toString().toLowerCase().replace(/\s+/g, "-").replace(/[^\w\-]+/g, "").replace(/\-\-+/g, "-").replace(/^-+/, "").replace(/-+$/, "");

const stripHtml = (html) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
};

const getShortDescription = (course) => {
    if (course.courseOneLiner) return course.courseOneLiner;
    const plainText = stripHtml(course.Description);
    return plainText.length > 120 ? plainText.substring(0, 120) + "..." : plainText;
};

const CategoryList = () => {
    const { categories, isLoading, error } = useSelector(state => state?.category);
    const { courses, isFetching } = useSelector(state => state?.course || []);
    const { loggedIn } = useSelector(state => state?.authentication || {});
    const courseList = courses?.all?.courses || [];
    const [showCategories, setShowCategories] = useState(false);
    const [activeCategoryName, setActiveCategoryName] = useState("");
    const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
    const [shareCourse, setShareCourse] = useState(null);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    useEffect(() => {
        const handleResize = () => setIsMobileView(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Set first sorted category as active by default on categories load
    useEffect(() => {
        if (categories && categories.length > 0) {
            const sorted = categories.slice().sort((a, b) => a.categoryName.localeCompare(b.categoryName));
            if (!activeCategoryName) {
                setActiveCategoryName(sorted[0]?.categoryName);
            }
        }
    }, [categories]);

    const handleViewProgram = (course) => {
        const slug = slugify(course.CourseName);
        console.log("Navigating to course:", slug, course);
        navigate(`/${slug}`, { state: { course } });
        setShowCategories(false);
    };

    const likeHandler = (e, courseId, isWishlisted) => {
        e.stopPropagation(); // Prevent card navigation
        if (loggedIn) {
            dispatch(courseActions.handleResetCourseList());
            if (isWishlisted) {
                dispatch(deleteWishlist(courseId));
            } else {
                dispatch(addWishlist(courseId));
            }
        }
         else {
            navigate("/login");
        }
    };

    const sortedCategories = categories
        ?.slice()
        .sort((a, b) => a.categoryName.localeCompare(b.categoryName));

    return (
        <div className="category-list-container">
            <button 
                className="px-4 py-2 bg-slate-50/50 hover:bg-slate-50 border border-slate-200/80 hover:border-slate-300 text-slate-700 font-semibold text-sm rounded-xl flex items-center gap-1.5 transition-all shadow-sm active:scale-95"
                onClick={() => setShowCategories(!showCategories)}
                onMouseEnter={() => !isMobileView && setShowCategories(true)}
            >
                <span>Explore</span>
                <span className="text-[9px] text-slate-400 font-bold transition-transform duration-200 group-hover:translate-y-0.5">▼</span>
            </button>

            {showCategories && (
                <>
                    <div className="blur-overlay"></div>
                    <div className="mega-menu" onMouseEnter={() => !isMobileView && setShowCategories(true)} onMouseLeave={() => !isMobileView && setShowCategories(false)}>
                        {isMobileView && activeCategoryName && (
                            <button className="back-btn" onClick={() => setActiveCategoryName(null)} style={{ marginLeft: "50px", marginTop: "20px" }}>
                                <FaArrowLeft style={{ float: "left" }} /> Back
                            </button>
                        )}

                        <div className="mega-menu-container">
                            {(!isMobileView || !activeCategoryName) && (
                                <div className="categories-section scrollbar-thin">
                                    {sortedCategories?.map((category, index) => (
                                        <button
                                            key={category.id || index}
                                            className={`tab-button ${activeCategoryName === category.categoryName ? "active" : ""}`}
                                            onClick={() => setActiveCategoryName(category.categoryName)}
                                            onMouseEnter={() => !isMobileView && setActiveCategoryName(category.categoryName)}
                                        >
                                            {category.categoryName}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {activeCategoryName && (
                                <div className="courses-section !overflow-y-auto">
                                    <div className="courses-grid scrollbar-thin">
                                        {courseList.filter(course => course.category === activeCategoryName).length > 0 ? (
                                            courseList.filter(course => course.category === activeCategoryName)
                                                .map((course, idx) => (
                                                    <div key={idx} className="course-card" onClick={() => handleViewProgram(course)}>
                                                        {/* Tag Ribbon */}
                                                        <div className="ribbon-wrapper">
                                                            <span className="ribbon-tag">
                                                                {course.courseType || "Online"}
                                                            </span>
                                                        </div>

                                                        {/* Floating Action Overlay (Wishlist & Share) */}
                                                        <div className="card-floating-actions" onClick={(e) => e.stopPropagation()}>
                                                            <button
                                                                className={`floating-btn wishlist-btn ${course?.isWishlisted ? "active" : ""}`}
                                                                onClick={(e) => likeHandler(e, course.ID, course?.isWishlisted)}
                                                                title={course?.isWishlisted ? "Remove from Wishlist" : "Add to Wishlist"}
                                                            >
                                                                <Heart size={15} fill={course?.isWishlisted ? "#ef4444" : "none"} stroke={course?.isWishlisted ? "#ef4444" : "currentColor"} />
                                                            </button>
                                                            <button
                                                                className="floating-btn share-btn"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setShareCourse(course);
                                                                }}
                                                                title="Share Program"
                                                            >
                                                                <Share2 size={15} />
                                                            </button>
                                                        </div>

                                                        <div className="card-top-row">
                                                            <div className="image-container">
                                                                <img
                                                                    src={getS3URL(course?.imageFiles[0]?.fileName)}
                                                                    alt={course?.CourseName}
                                                                    className="course-image"
                                                                    loading="lazy"
                                                                    onError={(e) => {
                                                                        e.target.onerror = null;
                                                                        e.target.src = require("@assets/images/bgimages.webp");
                                                                    }}
                                                                />
                                                            </div>
                                                            <h2 className="course-title">{course.CourseName}</h2>
                                                        </div>

                                                        <div className="card-stats-row">
                                                            <span className="stat-item">
                                                                <strong>Duration:</strong> {course.Duration} hrs
                                                            </span>
                                                            <span className="stat-item">
                                                                <strong>Starts:</strong>{" "}
                                                                {moment(course?.Createdat).format("MMM DD, YYYY")}
                                                            </span>
                                                        </div>

                                                        <div className="card-description">
                                                            <p>{getShortDescription(course)}</p>
                                                        </div>

                                                        <div className="card-actions-row ">
                                                            <button
                                                                className="view-button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleViewProgram(course);
                                                                }}
                                                            >
                                                                View Course
                                                            </button>
                                                            <div className="buy-button-wrapper" onClick={(e) => e.stopPropagation()}>
                                                                {loggedIn ? (
                                                                    <CartButton post={course} />
                                                                ) : (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            navigate("/login");
                                                                        }}
                                                                    >
                                                                        <span className="add-to-cart">{t("addToCart")}</span>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                        ) : (
                                            <div className="no-course">No Courses Found</div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
            {shareCourse && (
                <Share
                    shareUrl={`${window.location.origin}/${slugify(shareCourse.CourseName)}`}
                    handleClose={() => setShareCourse(null)}
                />
            )}
        </div>
    );
};
export default CategoryList;
